import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Exibe valores monetários mascarados (****) com olho para revelar.
 * Modo display: só leitura. Modo input: integra com formControlName / ngModel.
 */
@Component({
  selector: 'app-sensitive-currency',
  templateUrl: './sensitive-currency.component.html',
  styleUrls: ['./sensitive-currency.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SensitiveCurrencyComponent),
      multi: true
    }
  ]
})
export class SensitiveCurrencyComponent implements ControlValueAccessor {
  /** Quando true, não edita — só mostra valor/máscara + olho. */
  @Input() displayOnly = false;
  /** Atalho para bind unidirecional (ex.: tabelas e resumos). */
  @Input()
  set amount(value: number | null | undefined) {
    this.writeValue(value);
  }
  @Input() placeholder = '0,00';
  @Input() inputId = '';
  @Input() invalid = false;
  @Input() min: number | null = 0;
  @Input() step = '0.01';
  /** Texto da máscara (apresentação). */
  @Input() maskText = '****';
  @Input() readonly = false;
  @Output() valueChange = new EventEmitter<number | null>();

  revealed = false;
  disabled = false;
  value: number | null = null;

  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get formatted(): string {
    if (this.value === null || this.value === undefined || Number.isNaN(Number(this.value))) {
      return '—';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(this.value));
  }

  get maskedLabel(): string {
    return this.maskText;
  }

  toggle(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.revealed = !this.revealed;
  }

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.value = null;
      return;
    }
    const n = Number(value);
    this.value = Number.isNaN(n) ? null : n;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onNumberInput(raw: string | number): void {
    if (raw === '' || raw === null || raw === undefined) {
      this.value = null;
      this.onChange(null);
      this.valueChange.emit(null);
      return;
    }
    const n = Number(raw);
    this.value = Number.isNaN(n) ? null : n;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
  }

  markTouched(): void {
    this.onTouched();
  }
}
